import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Grid, List, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductGrid from "@/components/ProductGrid";

interface Product {
  id: string;
  title: string;
  title_translations?: Record<string, string>;
  price_cents: number;
  currency: string;
  images: string[] | null;
  vendor_id: string;
  stock: number;
  active: boolean;
  description: string | null;
  description_translations?: Record<string, string>;
  category: string | null;
  brand: string | null;
}

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Sync with URL params and trigger search
  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      searchProducts(query);
    } else {
      setSearchQuery('');
      setProducts([]);
    }
  }, [searchParams, sortBy]);

  // Real-time search as user types
  useEffect(() => {
    const query = searchQuery.trim();
    
    if (!query) {
      setProducts([]);
      return;
    }

    const timer = setTimeout(() => {
      // Only update URL if query is different from URL param
      const currentQuery = searchParams.get('q');
      if (query !== currentQuery) {
        setSearchParams({ q: query }, { replace: true });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setProducts([]);
      return;
    }

    try {
      setLoading(true);
      
      // Normalize query
      const normalizedQuery = query.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const searchTerms = normalizedQuery.split(/\s+/).filter(term => term.length >= 2);
      
      if (searchTerms.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      // Build PostgreSQL text search query
      const tsQuery = searchTerms.map(term => `${term}:*`).join(' & ');

      // Fetch all active products from approved vendors (we'll filter client-side for better scoring)
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          vendor:profiles!vendor_id(vendor_status)
        `)
        .eq('active', true);

      if (error) throw error;

      // Client-side scoring with STRICT relevance
      const scoredProducts = (data || [])
        // First filter to only approved vendors
        .filter(product => product.vendor && product.vendor.vendor_status === 'approved')
        .map(product => {
        const title = (product.title || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const description = (product.description || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const brand = (product.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const category = (product.category || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        
        // Get translations
        const titleEn = (product.title_translations?.en || '').toLowerCase();
        const titleFr = (product.title_translations?.fr || '').toLowerCase();

        let score = 0;
        let hasRelevantMatch = false; // Must match in title or brand to be relevant

        // Split into words for word-boundary matching
        const titleWords = title.split(/\s+/).filter(w => w.length > 0);
        const brandWords = brand.split(/\s+/).filter(w => w.length > 0);
        
        // EXACT MATCHES (highest priority)
        if (title === normalizedQuery) {
          score += 10000;
          hasRelevantMatch = true;
        }
        if (titleEn === normalizedQuery || titleFr === normalizedQuery) {
          score += 8000;
          hasRelevantMatch = true;
        }
        
        // STARTS WITH (very high priority)
        if (title.startsWith(normalizedQuery)) {
          score += 5000;
          hasRelevantMatch = true;
        }
        if (brand.startsWith(normalizedQuery)) {
          score += 3000;
          hasRelevantMatch = true;
        }
        
        // Check each search term
        searchTerms.forEach(term => {
          // EXACT WORD MATCHES (word boundaries)
          if (titleWords.includes(term)) {
            score += 1000;
            hasRelevantMatch = true;
          }
          if (brandWords.includes(term)) {
            score += 800;
            hasRelevantMatch = true;
          }
          
          // WORD STARTS WITH TERM
          if (titleWords.some(w => w.startsWith(term))) {
            score += 500;
            hasRelevantMatch = true;
          }
          if (brandWords.some(w => w.startsWith(term))) {
            score += 400;
            hasRelevantMatch = true;
          }
          
          // CONTAINS IN TITLE (lower priority)
          if (title.includes(term)) {
            score += 200;
            hasRelevantMatch = true;
          }
          
          // CONTAINS IN TRANSLATIONS
          if (titleEn.includes(term) || titleFr.includes(term)) {
            score += 150;
            hasRelevantMatch = true;
          }
          
          // CONTAINS IN BRAND
          if (brand.includes(term)) {
            score += 100;
            hasRelevantMatch = true;
          }
          
          // Category and description - ONLY if already relevant
          if (hasRelevantMatch) {
            if (category.includes(term)) score += 10;
            if (description.includes(term)) score += 5;
          }
        });

        // FULL QUERY IN TITLE
        if (title.includes(normalizedQuery)) {
          score += 2000;
          hasRelevantMatch = true;
        }
        if (brand.includes(normalizedQuery)) {
          score += 1500;
          hasRelevantMatch = true;
        }

        // ALL TERMS MUST MATCH for multi-word queries
        if (searchTerms.length > 1) {
          const matchedInTitle = searchTerms.filter(term => 
            title.includes(term) || titleEn.includes(term) || titleFr.includes(term)
          ).length;
          
          if (matchedInTitle === searchTerms.length) {
            score += 500 * searchTerms.length;
          } else {
            // Penalize if not all terms match
            score = score * (matchedInTitle / searchTerms.length);
          }
        }

        return { product, score, hasRelevantMatch };
      });

      // STRICT FILTERING: Must have relevant match in title/brand AND minimum score
      let filteredProducts = scoredProducts
        .filter(item => item.hasRelevantMatch && item.score >= 100)
        .sort((a, b) => b.score - a.score)
        .map(item => item.product);

      // Apply additional sorting if selected
      if (sortBy !== 'relevance') {
        switch (sortBy) {
          case 'price_low':
            filteredProducts.sort((a, b) => a.price_cents - b.price_cents);
            break;
          case 'price_high':
            filteredProducts.sort((a, b) => b.price_cents - a.price_cents);
            break;
          case 'name':
            filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
            break;
        }
      }

      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchProducts(searchQuery);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-8">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">
              {searchQuery.trim() ? (
                <>
                  {t('search.results_for')}: "{searchQuery}"
                </>
              ) : (
                t('search.title')
              )}
            </h1>
            {searchQuery.trim() && (
              <p className="text-muted-foreground mt-1">
                {loading ? t('search.searching') : `${products.length} ${products.length === 1 ? t('search.result') : t('search.results')}`}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Sort Options */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('search.sort_by')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">{t('search.sort.relevance')}</SelectItem>
                <SelectItem value="price_low">{t('search.sort.price_low')}</SelectItem>
                <SelectItem value="price_high">{t('search.sort.price_high')}</SelectItem>
                <SelectItem value="name">{t('search.sort.name')}</SelectItem>
              </SelectContent>
            </Select>

            {/* View Mode Toggle */}
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('search.searching')}</p>
            </div>
          </div>
        ) : searchQuery.trim() ? (
          products.length > 0 ? (
            <ProductGrid products={products} viewMode={viewMode} />
          ) : (
            <div className="text-center py-20">
              <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">{t('search.no_results')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('search.try_different')}
              </p>
              <Button onClick={() => { setSearchQuery(''); navigate('/search', { replace: true }); }}>
                {t('search.browse_all')}
              </Button>
            </div>
          )
        ) : (
          <div className="text-center py-20">
            <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">{t('search.start_searching')}</h2>
            <p className="text-muted-foreground">
              {t('search.enter_keywords')}
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SearchPage;
